import React from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';

import { useListAccessManagementUsers } from '@hooks/api/access-permission';
import { useSession } from '@hooks/useSession';

import { formatName } from '@utils/format';
import { mapProfile } from '@utils/profiles';

import UserAccessDrilldown from '@views/ManageAccount/AccessManagement/UserAccessDrilldown';
import ManageAccountLayout from '@views/ManageAccount/ManageAccountLayout';

const messages = defineMessages({
  title: {
    id: 'manageAccount.accessManagement',
    defaultMessage: 'Access Management',
  },
  name: {
    id: 'GLOBAL.name',
    defaultMessage: 'Name',
  },
});

function AccessManagementDrilldownPage(props: PagePropsWithBreadcrumbs) {
  const { formatMessage } = useIntl();
  const {
    state: { activeProfile },
  } = useSession();
  const { data: accessUsers, loading } = useListAccessManagementUsers(
    activeProfile?.user_id
  );

  const user = accessUsers.find(({ user_id }) => user_id === props.params.id);
  const accessingUser = mapProfile(user) ?? undefined;
  const updatedCrumbs = props.pageContext.breadcrumb.crumbs.map((crumb) => {
    if (crumb.crumbLabel === '[id]') {
      return {
        ...crumb,
        crumbLabel:
          formatName(accessingUser?.first_name, accessingUser?.last_name) ??
          formatMessage(messages.name),
        pathname: props.uri,
      };
    }
    return crumb;
  });

  const updatedProps = {
    ...props,
    pageContext: {
      breadcrumb: {
        ...props.pageContext.breadcrumb,
        crumbs: updatedCrumbs,
      },
    },
  };

  return (
    <ManageAccountLayout
      friendlyName={formatMessage(messages.title)}
      pageProps={updatedProps}
    >
      {({ user }) => (
        <UserAccessDrilldown
          accessingUser={accessingUser}
          loading={loading}
          ownerUser={user}
        />
      )}
    </ManageAccountLayout>
  );
}

export default withErrorBoundary(AccessManagementDrilldownPage, {
  FallbackComponent: ErrorBoundaryFallback,
});
